import React, { ReactElement, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Redirect, withRouter } from 'react-router'

import { IonGrid, IonRow, IonFooter } from '@ionic/react'

import {
	TourStopsContainer as Container,
	CollectionItem as CollectionItemType,
	COLLECTION_ITEM_SUBTYPE_WEB,
	COLLECTION_ITEM_SUBTYPE_STORY,
	COLLECTION_ITEM_SUBTYPE_PANORAMA,
	KeypadEntryStatus,
	COLLECTION_ITEM_SUBTYPE_LIST,
	COLLECTION_ITEM_SUBTYPE_MENU,
	COLLECTION_ITEM_SUBTYPE_TOUR,
	COLLECTION_ITEM_TYPE_SCREEN,
	RootStoreState,
	Collection,
	isCollectionBundleLocked,
	offeredBundlesFor,
} from 'mytours-read-common'

import { CollectionItemsNavigationParams, BoundDirection, CollectionItemsRouteParams } from '../types'
import { layoutMode, stopHash } from '../functions'
import useTourMapModal from '../hooks/useTourMapModal'

import { Button } from '@mytours/components'

import { ionHistory } from '../../navigation'
import { getTabsUrl, getTourUrl } from '../../navigation/routes'
import NavigationService from '../../navigation/NavigationService'
import MenuList from '../../common/components/menu/MenuList'

import Page from '../../common/components/Page'
import { Props as HeaderProps } from '../../common/components/header'

import { ImageSizePreset } from '../../../common_lib/types'
import { generateImageUrl } from 'common_lib/utils'
import usePrevious from 'common_lib/hooks/use-previous'
import { EventActionsEnum, sendEvent } from 'mobile_web/modules/logging/analytics'
import { GetCollectionItemsIndexResponseData, RedeemCode, MapRoute } from 'mytours-read-api-typescript'
import { validateCollectionCode } from 'mobile_web/modules/lockapp/functions'
import ScannerModal from 'mobile_web/modules/scanner/components/ScannerModal'
import MetaHeader from '../parts/MetaHeader'
import TourStopFooter from '../parts/tour-stop-footer'
import MapLayout from 'mobile_web/modules/map/parts/MapActionsLayout'
import Modal from 'mobile_web/modules/common/components/modal'
import { loadingTimeout } from 'mobile_web/modules/common/functions'
import StoryContentPlaceholder from 'mobile_web/modules/common/components/placeholders/StoryContent'
import ImagePlaceholder from 'mobile_web/modules/common/components/placeholders/Image'
import DefaultPagePlaceholder from 'mobile_web/modules/common/components/placeholders/DefaultPage'
import { getPlaceholderTypeByPathname } from 'mobile_web/modules/common/components/placeholders/functions'
import Content from 'mobile_web/modules/common/components/content'

import strings from '../../../strings'

import classNames from 'classnames'
import Page404 from 'mobile_web/404'
import './styles/collectionItems.less'
import CollectionListComponent from './CollectionList'
import ScreenStoryComponent from 'mobile_web/modules/screen/components/ScreenStory'
import ScreenPanoramaComponent from 'mobile_web/modules/screen/components/ScreenPanorama'
import ScreenWebComponent from 'mobile_web/modules/screen/components/ScreenWeb'
import CollectionTourComponent from './CollectionTour'
import AudioPlayerBarComponent from 'mobile_web/modules/audio/components/AudioPlayerBar'
import LockAppComponent from 'mobile_web/modules/lockapp/components/LockApp'
import BundleUnlockButton from 'mobile_web/modules/lockapp/components/BundleUnlockButton'
import BundleOnlyLock from 'mobile_web/modules/lockapp/components/BundleOnlyLock'

type Props = Container.Props & Container.Actions & CollectionItemsRouteParams & CollectionItemsNavigationParams

const CollectionItems: React.FC<Props> = props => {
	const [itemVersion, setItemVersion] = useState<number>(0)
	const [locationHash, setLocationHash] = useState<string | undefined>()

	const isTour = props.match.params.type === GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Tour

	const showMapLayout = useMemo<boolean>(() => {
		const { collection = {} as Collection } = props
		const { initial_layout: initialLayout } = collection

		return layoutMode(initialLayout) === 'map'
	}, [props.collection?.initial_layout])

	const collectionItems = useMemo<CollectionItemType[]>(() => {
		if (!isTour) {
			return props.collectionItems
		}

		return props.collectionItems?.filter(item => item.item_type === COLLECTION_ITEM_TYPE_SCREEN)
	}, [props.collectionItems])

	const [showScanner, setShowScanner] = useState<boolean>(false)
	const [collectionItemIndex, setCollectionItemIndex] = useState<number>(null)
	const [codeLock, setCodeLock] = useState<boolean>(null)
	const [codeValidationStarted, setCodeValidationStarted] = useState<boolean>(false)
	const [loading, setLoading] = useState<boolean>(collectionItems === undefined)

	const mapModal = useTourMapModal({
		hasMap: props.hasMap,
		isMapLayout: showMapLayout,
		loading,
		isEmpty: !!collectionItems && collectionItems.length === 0,
	})

	const activeItem = collectionItems?.[collectionItemIndex || 0]
	const activeScreen = useSelector((state: RootStoreState) => state.screen.screens.get(activeItem?.item_id))?.screen

	const sendSelectStopEvent = (method: 'keypad' | 'qr-code' | 'hamburger', index: number) => {
		const { collection } = props
		sendEvent(EventActionsEnum.SelectItem, {
			collection: collection?.name,
			collectionId: collection?.id,
			itemName: collectionItems?.[index]?.name,
			method,
		})
	}

	const getItemIndexById = (id: number): number => {
		const { match } = props
		const {
			params: { item: itemParam },
		} = match

		return collectionItems?.findIndex(
			// check `item_id` for tours stop; `id` for any collection item
			item => (itemParam === 'item' && item.id === id) || (item.item_subtype === itemParam && item.item_id === id),
		)
	}

	const getItemIdByIndex = (index: number): number | undefined => {
		if (!collectionItems[index]) {
			return
		}

		// setCurrentPage always navigates to an `item` route, so this must be the
		// `id` (CollectionItemId), even when the current route uses a subtype
		// (e.g. `/tour/1/story/2` after a keypad entry)
		return collectionItems[index].id
	}

	const setCurrentPage = (pageIndex: number, stateToUpdate: Record<string, string | number> = {}) => {
		const { collectionId, collectionVersion, collectionTitle, title, collectionAccountId } = props

		const queryParams = {}

		if (stateToUpdate.action === 'menu' && (showMapLayout || mapModal.isOpen)) {
			queryParams['show-detail-modal'] = 'true'
		}

		NavigationService.navigateToCollectionItems({
			params: {
				collectionId,
				collectionVersion,
				itemId: getItemIdByIndex(pageIndex),
				collectionTitle,
				title,
				collectionAccountId,
			},
			collectionType: GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Tour,
			itemType: 'item',
			stateToUpdate,
			queryParams,
			isOnlyChangePage: true,
		})
	}

	const getPageNumber = (): number => {
		const {
			match: { params },
		} = props

		if (!params || (params && (!params.itemId || !params.item))) {
			return params.type === 'tour' ? 0 : -1
		}

		return getItemIndexById(Number(params.itemId))
	}

	const changeStopByItemId = (itemId: number) => {
		// keypad items reference the screen (`item_id`), not the CollectionItemId
		const collectionItemIndex = collectionItems?.findIndex(item => item.item_id === itemId)

		if (collectionItemIndex > -1) {
			sendSelectStopEvent('keypad', collectionItemIndex)
			setCurrentPage(collectionItemIndex)
			setShowScanner(false)
		}
	}

	useLayoutEffect(() => {
		if (ionHistory?.location?.hash) {
			// handle openning stop from qr code scanner
			if (ionHistory?.location?.hash === '#qr-code') {
				setLocationHash('qr-code')
				ionHistory.replace(ionHistory?.location?.pathname)
			}
		}
	}, [])

	useEffect(() => {
		if (!props.collectionVersion) return
		if (collectionItems === undefined) {
			setItemVersion(props.collectionVersion)
			props.downloadTour(props.collectionVersion, props.language, 'tour', props.collectionAccountId)
		}

		props.loadCollectionDetails(props.collectionId, props.collectionVersion, props.language, props.collectionAccountId)
	}, [props.collectionVersion])

	useEffect(() => {
		if (props.collection?.version && props.collection?.version != itemVersion) {
			setItemVersion(props.collection?.version)
			props.downloadTour(props.collection?.version, props.language, 'tour', props.collectionAccountId)
		}
	}, [props.collection?.version, itemVersion])

	const prevPropsMatchParams: any = usePrevious(props.match.params)
	useEffect(() => {
		if (
			collectionItems &&
			(prevPropsMatchParams?.itemId != props.match.params?.itemId || collectionItemIndex === null)
		) {
			setCollectionItemIndex(getPageNumber())
		}
	}, [collectionItems, prevPropsMatchParams?.itemId, props.match.params?.itemId, collectionItemIndex])

	const prevPropsKeypadEntryStatus: KeypadEntryStatus = usePrevious(props.keypadEntryStatus)
	useEffect(() => {
		if (
			prevPropsKeypadEntryStatus === KeypadEntryStatus.Processing &&
			!props.keypadEntryStatus &&
			props.keypadEntryItems.length === 1 &&
			!props.keypadEntryLoading &&
			layoutMode(props.collection.initial_layout) === 'default'
		) {
			changeStopByItemId(props.keypadEntryItems[0].id)
		}
	}, [prevPropsKeypadEntryStatus, props.keypadEntryStatus, props.keypadEntryItems, props.keypadEntryLoading])

	useEffect(() => {
		if (locationHash === 'qr-code' && props.collection && collectionItems) {
			setLocationHash(undefined)
			sendSelectStopEvent('qr-code', getPageNumber())
		}
	}, [locationHash, props.collection, collectionItems])

	useEffect(() => {
		if (!props.collection) return

		if (codeLock === null && props.collection?.hasOwnProperty('code_lock')) {
			// TODO: add additional condition to check if code is loading
			// A collection inside a redeemed bundle is unlocked regardless of its own code gate.
			setCodeLock(props.collection?.code_lock && !props.unlockedViaBundle)
		}
	}, [props.collection?.code_lock])

	// If a bundle redemption unlocks this collection after its lock was already applied, clear the
	// gate so the stops become accessible without a collection code.
	useEffect(() => {
		if (props.unlockedViaBundle && codeLock) {
			setCodeLock(false)
		}
	}, [props.unlockedViaBundle, codeLock])

	const prevStateCodeLock: boolean = usePrevious(codeLock)
	useEffect(() => {
		if (prevStateCodeLock === codeLock) return
		;(async () => {
			if (!codeValidationStarted && props.collection?.code_lock && !props.unlockedViaBundle) {
				setCodeValidationStarted(true)
				await validateCollectionCode(props.collection, props.validateCode)
			}
		})()
	}, [codeLock])

	const prevPropsLockCollection: any = usePrevious(props.lockCollection.status)
	useEffect(() => {
		const { lockCollection, collection, validateCode } = props
		if (!lockCollection.loading && lockCollection.loaded) {
			if (lockCollection.status === 'valid' && !prevPropsLockCollection) {
				setCodeLock(false)
			} else if (
				(lockCollection.status === 'invalid' || lockCollection.status === 'expired') &&
				!prevPropsLockCollection
			) {
				validateCollectionCode(collection, validateCode)
			}
		}
	}, [props.lockCollection, props.collection])

	useEffect(() => {
		const isLoading: boolean = collectionItems === undefined || props.lockCollection?.loading

		if (isLoading !== loading) {
			// loading with timeout to change skeleton with images content smoothly
			loadingTimeout(isLoading, val => setLoading(val))
		}
	}, [collectionItems, props.lockCollection.loading, loading])

	const renderItem = (collectionItem: CollectionItemType = {}, isDirectRender: boolean): ReactElement => {
		const { item_subtype: itemSubtype, item_id: itemId, account_id: accountId, version } = collectionItem
		const { collection = {} as Collection } = props
		switch (itemSubtype) {
			case COLLECTION_ITEM_SUBTYPE_WEB:
				return (
					<ScreenWebComponent
						itemId={itemId}
						itemVersion={version}
						isPageContent={!isDirectRender}
						accountId={accountId}
					/>
				)
			case COLLECTION_ITEM_SUBTYPE_STORY:
				return (
					<ScreenStoryComponent
						itemId={itemId}
						itemVersion={version}
						isPageContent={!isDirectRender}
						collection={collection}
						accountId={accountId}
					/>
				)
			case COLLECTION_ITEM_SUBTYPE_PANORAMA:
				return (
					<ScreenPanoramaComponent
						itemId={itemId}
						itemVersion={version}
						isPageContent={!isDirectRender}
						collectionId={collection.id}
						accountId={accountId}
					/>
				)
			case COLLECTION_ITEM_SUBTYPE_LIST:
			case COLLECTION_ITEM_SUBTYPE_MENU:
				return <CollectionListComponent collectionId={itemId} collectionVersion={version} accountId={accountId} />
			case COLLECTION_ITEM_SUBTYPE_TOUR:
				return <CollectionTourComponent collectionId={itemId} collectionVersion={version} accountId={accountId} />
			default:
				return <div />
		}
	}

	const hasPrevious = useMemo<boolean>(() => collectionItemIndex > 0, [collectionItemIndex])

	const hasNext = useMemo<boolean>(
		() => !!collectionItems && collectionItemIndex < collectionItems.length - 1,
		[collectionItems, collectionItemIndex],
	)

	const onChooseStopFromMenu = (id: number) => {
		// MenuList passes the CollectionItemId regardless of the current route shape
		const pageIndex = collectionItems?.findIndex(item => item.id === id)
		sendSelectStopEvent('hamburger', pageIndex)
		setCurrentPage(pageIndex, { action: 'menu' })
	}

	const renderMenuContent = (): JSX.Element => {
		const activePage = collectionItems?.[collectionItemIndex]?.id

		return <MenuList list={collectionItems} activePageId={activePage} navigateToItem={onChooseStopFromMenu} />
	}

	const onTourExit = () => {
		// TODO: Property 'prevPagePath' does not exist on type 'unknown'
		const { state }: any = ionHistory.location
		const prevPagePath = state?.prevPagePath || getTabsUrl()

		ionHistory.replace(prevPagePath)
	}

	const changeScreenAction = (direction: BoundDirection) => {
		let index

		if (direction == 'prev') {
			index = collectionItemIndex - 1 > -1 ? collectionItemIndex - 1 : collectionItems.length - 1
		} else if (direction == 'next') {
			index = collectionItemIndex + 1 < collectionItems.length ? collectionItemIndex + 1 : 0
		}

		setCurrentPage(index)
	}

	const toggleScannerModal = () => {
		setShowScanner(!showScanner)
	}

	const getHeaderProps = (): HeaderProps | undefined => {
		const { collection = {} as Collection, match } = props
		const { title = '' } = collection

		const {
			params: { type: collectionType },
		} = match

		const currentStop: CollectionItemType = collectionItems?.[collectionItemIndex]

		switch (collectionType) {
			case GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Tour:
				return {
					menuTitle: title,
					type: showMapLayout ? 'hidden-menu' : 'menu',
					defaultScrolled: true,
					showTourExitButton: true,
					renderMenuContent,
					collection,
					buttonTextShadow: false,
					onBackButton: onTourExit,

					collectionItem: currentStop,
					screen: null,
					itemId: currentStop?.item_id,
					itemType: currentStop?.item_type,
					hideFavouriteButton: false,
				}
			case GetCollectionItemsIndexResponseData.ItemSubtypeEnum.List:
			case GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Menu:
				// when `list` or `menu` then render screen component directly, without additional header

				return undefined
			default:
				return undefined
		}
	}

	const renderMapContent = ({ pageIndex, showMapLayoutParam }): JSX.Element => {
		const { collection = {} as Collection, collectionAccountId } = props
		const { keypad_enabled: keypadEnabled } = collection

		return (
			<MapLayout
				accountId={collectionAccountId}
				details={collection}
				items={collectionItems}
				currentItemIndex={pageIndex}
				onCloseMap={!showMapLayoutParam && mapModal.toggle}
				setCurrentPage={setCurrentPage}
				isMapLayout={showMapLayoutParam}
				keypadEnabled={keypadEnabled}
				onExit={onTourExit}
				renderToggleMenuContent={renderMenuContent}
				itemType={MapRoute.LinkedTypeEnum.Collection}
				onSelectItemFromMenu={onChooseStopFromMenu}
			/>
		)
	}

	const renderModal = (): JSX.Element => {
		const currentStop: CollectionItemType = collectionItems?.[collectionItemIndex]

		if (!collectionItems || !currentStop) {
			return null
		}

		return (
			<Modal
				isOpen={mapModal.isOpen}
				cssClass="map-modal"
				backdropDismiss={false}
				showBackdrop={false}
				onDidPresent={() => mapModal.onDidPresent(true)}
				onDidDismiss={() => mapModal.onDidPresent(false)}
			>
				<Content>
					{showMapLayout && (
						<IonRow className="close-map-button-container">
							<Button
								variant="clear"
								fill="var(--tab-bg)"
								textColor="var(--body-color)"
								className="close-map-button list-view"
								onClick={mapModal.toggle}
							>
								<i className="fal fa-xmark" />
							</Button>
						</IonRow>
					)}
					<IonGrid className={classNames('map-modal-content', showMapLayout && 'list-content')}>
						{showMapLayout
							? renderItem(currentStop, false)
							: mapModal.didPresent && renderMapContent({ pageIndex: collectionItemIndex, showMapLayoutParam: false })}
					</IonGrid>
				</Content>
				<IonFooter>{mapModal.isOpen && <AudioPlayerBarComponent bottomOffset={0} className="full-width" />}</IonFooter>
			</Modal>
		)
	}

	const renderScanner = () => {
		const { collection = {} as Collection } = props

		return (
			<ScannerModal
				qrEnabled={collection.qr_enabled}
				keypadEnabled={collection.keypad_enabled}
				showScanner={showScanner}
				toggleScannerModal={toggleScannerModal}
				itemId={collection.id}
			/>
		)
	}

	const renderPlaceholder = (): JSX.Element => {
		const placeholderStoryType = getPlaceholderTypeByPathname(ionHistory.location.pathname)

		return [COLLECTION_ITEM_SUBTYPE_PANORAMA, COLLECTION_ITEM_SUBTYPE_WEB].includes(placeholderStoryType) ||
			showMapLayout ? (
			<ImagePlaceholder style={{ height: '100%' }} />
		) : (
			<StoryContentPlaceholder style={{ height: 'calc(100% - 4px)', width: '100%', padding: '0 24px 24px' }} />
		)
	}

	const renderPageContent = (isEmpty: boolean) => {
		const activePage = collectionItems?.[collectionItemIndex]

		const isLoadingContent = loading || mapModal.isOpening || codeLock
		const showContent = !isEmpty && collectionItems !== undefined && activePage

		if (isLoadingContent) return renderPlaceholder()

		return (
			<>
				{isEmpty && (
					<IonGrid className="empty-grid">
						<IonRow>{strings.no_stops}</IonRow>
					</IonGrid>
				)}
				{showContent &&
					(showMapLayout
						? renderMapContent({ pageIndex: collectionItemIndex, showMapLayoutParam: showMapLayout })
						: renderItem(activePage, false))}
				{renderScanner()}
			</>
		)
	}

	const { collection = {} as Collection, hasMap, lockCollection, match, error } = props

	const {
		title,
		description,
		code_title: codeTitle,
		code_text: codeText,
		code_background_media_item: codeBackgroundMediaItem,
		qr_enabled: qrEnabled,
		keypad_enabled: keypadEnabled,
	} = collection

	const {
		params: { type: collectionType, itemId },
	} = match

	// A bundle force-lock keeps the stops shut even when the collection itself carries no code, and the
	// bundles that sell it are offered either way — beside the collection's own gate, or as the whole gate.
	const bundleLocked = isCollectionBundleLocked(collection, props.unlockedViaBundle, lockCollection)
	const unlockingBundles = codeLock || bundleLocked ? offeredBundlesFor(collection) : []

	const isEmpty: boolean = !!collectionItems && collectionItems.length === 0

	// A single page tour has no pager: every stop is a row on the tour page, opened by `#stop-<item id>`.
	// An item route still arrives here from a QR scan, a keypad entry or a shared link, so send it on to
	// the row it names rather than opening the one-stop-at-a-time view this layout replaces.
	//
	// The wait is for the stops themselves: the route addresses a stop either by its collection item id
	// or by its item id, depending on the `item` segment, and only the downloaded list tells the two
	// apart. A stop the list does not hold — a code that has since been retired — lands on the tour with
	// nothing open, which is where the reader can find their way from.
	if (isTour && layoutMode(collection.initial_layout) === 'singlePage') {
		if (!collectionItems) {
			return <DefaultPagePlaceholder isTourPage={true} />
		}

		const stop = collectionItems[getItemIndexById(Number(itemId))]

		return (
			<Redirect
				to={`${getTourUrl(props.collectionId, props.collectionAccountId)}${stop ? stopHash(stop.item_id) : ''}`}
			/>
		)
	}

	if (!loading && (collectionItemIndex === -1 || error?.status === 404)) {
		return <Page404 />
	}

	if (!codeLock && bundleLocked) {
		return <BundleOnlyLock bundles={unlockingBundles} accountId={collection.account_id} />
	}

	if (codeLock && !lockCollection.loading) {
		return (
			<LockAppComponent
				linkedId={collection.id}
				linkedType={RedeemCode.LinkedTypeEnum.Collection}
				title={codeTitle}
				description={codeText}
				backgroundImage={codeBackgroundMediaItem}
				footer={<BundleUnlockButton bundles={unlockingBundles} accountId={collection.account_id} />}
			/>
		)
	} else if (codeLock && lockCollection.loading) {
		return <DefaultPagePlaceholder isTourPage={true} />
	}

	const activePage = collectionItems?.[getItemIndexById(Number(itemId))]

	if (
		!loading &&
		activePage &&
		(collectionType === GetCollectionItemsIndexResponseData.ItemSubtypeEnum.List ||
			collectionType === GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Menu)
	) {
		return renderItem(activePage, true)
	}

	const panoramaScreenClass =
		activePage?.item_subtype === GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Panorama && !showMapLayout
			? 'panorama-screen'
			: ''

	const activeItemSubtype = activeItem?.item_subtype
	const hasFullHeight =
		activeItemSubtype === GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Panorama ||
		activeItemSubtype === GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Web

	return (
		<>
			<MetaHeader title={title} description={description} pageEndIndex={collectionItems?.length} />
			<Page
				headerProps={{ ...getHeaderProps(), loading }}
				contentProps={{
					className: classNames(
						'tour-stops-page',
						panoramaScreenClass,
						showMapLayout && 'map-layout',
						hasFullHeight && 'full-height',
					),
					style:
						activePage?.item_subtype === GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Web
							? { '--content-background-image': 'url()' }
							: activeScreen?.background_image
								? {
										'--content-background-image': `url(${generateImageUrl(activeScreen.background_image?.file, {
											imageSizePreset: ImageSizePreset.Fullscreen,
											focalPoint: activeScreen.background_image.file?.focal_point,
										})})`,
									}
								: undefined,
				}}
				footer={
					!loading && {
						component: (
							<>
								{showMapLayout && <AudioPlayerBarComponent bottomOffset={0} className="full-width" />}
								{!showMapLayout && (
									<TourStopFooter
										hasPrev={hasPrevious}
										onPrevClick={() => changeScreenAction('prev')}
										hasMap={hasMap}
										onMapClick={mapModal.toggle}
										keypadEnabled={keypadEnabled}
										qrEnabled={qrEnabled}
										onScanClick={toggleScannerModal}
										hasNext={hasNext}
										onNextClick={() => changeScreenAction('next')}
										onFinishClick={onTourExit}
										collectionType={collectionType}
									/>
								)}
							</>
						),
						props: { className: 'tour-stops-page footer' },
					}
				}
				dataItemId={String(collection.id)}
				dataItemType="collection"
				dataItemSubtype="tour"
				dataCy="page-collection-items"
			>
				{renderPageContent(isEmpty)}
			</Page>
			{!loading && !showMapLayout && <AudioPlayerBarComponent />}

			{hasMap && renderModal()}
		</>
	)
}

const CollectionItemsComponent = Container.default(withRouter(CollectionItems))
export default CollectionItemsComponent
